import { useEffect, useState, useSyncExternalStore } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TrendingUp, Table as TableIcon, LineChart as LineChartIcon } from 'lucide-react';
import { statsApi } from '@/services/statistics';
import { formatCurrency } from '@/utils/document';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

interface TrendMonth {
  month: string;
  label: string;
  revenue: number;
  billable_hours: number;
  invoiced: number;
  paid: number;
}

type RangeKey = '6m' | '12m' | 'ytd' | '24m' | 'all';
type ViewKey = 'amounts' | 'hours';

const RANGE_OPTIONS: Array<{ value: RangeKey; label: string }> = [
  { value: '6m', label: 'Last 6 months' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'ytd', label: 'This year' },
  { value: '24m', label: 'Last 24 months' },
  { value: 'all', label: 'All time' },
];

function rangeStart(key: RangeKey, now: Date): Date {
  if (key === 'ytd') return new Date(now.getFullYear(), 0, 1);
  // The backend clamps the window to the firm's first activity.
  if (key === 'all') return new Date(2000, 0, 1);
  const back = key === '6m' ? 5 : key === '12m' ? 11 : 23;
  return new Date(now.getFullYear(), now.getMonth() - back, 1);
}

// Categorical palette, CVD-validated against both surfaces
// (dataviz validator: all checks pass in light and dark).
const SERIES = [
  { key: 'revenue', name: 'Work value', light: '#2563eb', dark: '#4c86e8' },
  { key: 'invoiced', name: 'Invoiced', light: '#d97706', dark: '#c08427' },
  { key: 'paid', name: 'Collected', light: '#059669', dark: '#2aa375' },
] as const;

const HOURS_COLOR = { light: '#2563eb', dark: '#4c86e8' };

// Re-render when the .dark class flips so series colors follow the theme.
function subscribeTheme(cb: () => void) {
  const observer = new MutationObserver(cb);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  return () => observer.disconnect();
}
const useIsDark = () =>
  useSyncExternalStore(subscribeTheme, () =>
    document.documentElement.classList.contains('dark'),
  );

interface RevenueTrendsChartProps {
  firmId: number;
  hideAmounts: boolean;
}

export function RevenueTrendsChart({ firmId, hideAmounts }: RevenueTrendsChartProps) {
  const [range, setRange] = useState<RangeKey>('6m');
  const [view, setView] = useState<ViewKey>('amounts');
  const [showTable, setShowTable] = useState(false);
  // Work value is hidden by default; the legend chips toggle series.
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set(['revenue']));
  const [data, setData] = useState<TrendMonth[]>([]);
  const [loading, setLoading] = useState(true);
  const isDark = useIsDark();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    statsApi
      .getMonthlyTrends({
        firm_id: firmId,
        start_date: format(rangeStart(range, new Date()), 'yyyy-MM-dd'),
        end_date: format(new Date(), 'yyyy-MM-dd'),
      })
      .then((res) => {
        if (!cancelled) setData(res.months);
      })
      .catch(() => {
        if (!cancelled) setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [firmId, range]);

  const money = (v: number) => (hideAmounts ? '•••' : formatCurrency(v));
  const moneyCompact = (v: number) =>
    hideAmounts ? '•••' : `€${Intl.NumberFormat('en', { notation: 'compact' }).format(v)}`;
  const hoursFmt = (v: number) => `${Math.round(v)}h`;

  const axisTick = { fontSize: 11, fill: 'var(--muted-foreground)' };
  const seriesColor = (s: (typeof SERIES)[number]) => (isDark ? s.dark : s.light);

  const tooltip = (
    <Tooltip
      cursor={{ stroke: 'var(--muted-foreground)', strokeDasharray: '3 3' }}
      content={({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        return (
          <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
            <div className="mb-1 font-medium text-foreground">{label}</div>
            {payload.map((p) => (
              <div key={String(p.dataKey)} className="flex items-center gap-2 py-0.5">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: p.color ?? p.stroke }}
                />
                <span className="text-muted-foreground">{p.name}</span>
                <span className="ml-auto pl-4 font-medium tabular-nums text-foreground">
                  {view === 'amounts' ? money(Number(p.value) || 0) : hoursFmt(Number(p.value) || 0)}
                </span>
              </div>
            ))}
          </div>
        );
      }}
    />
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Revenue & billing trends
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-md border p-0.5">
              <Button
                variant={view === 'amounts' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2.5 text-xs"
                onClick={() => setView('amounts')}
              >
                €
              </Button>
              <Button
                variant={view === 'hours' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2.5 text-xs"
                onClick={() => setView('hours')}
              >
                Hours
              </Button>
            </div>
            <Select value={range} onValueChange={(v) => setRange(v as RangeKey)}>
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              title={showTable ? 'Show chart' : 'Show as table'}
              onClick={() => setShowTable((s) => !s)}
            >
              {showTable ? <LineChartIcon className="h-4 w-4" /> : <TableIcon className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        {/* Legend — clickable chips toggle each series on and off */}
        {view === 'amounts' && !showTable && (
          <div className="flex flex-wrap gap-2 pt-1">
            {SERIES.map((s) => {
              const hidden = hiddenSeries.has(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  aria-pressed={!hidden}
                  onClick={() =>
                    setHiddenSeries((prev) => {
                      const next = new Set(prev);
                      if (next.has(s.key)) next.delete(s.key);
                      else next.add(s.key);
                      return next;
                    })
                  }
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                    hidden
                      ? 'border-transparent text-muted-foreground/60 hover:text-muted-foreground'
                      : 'text-muted-foreground hover:bg-accent'
                  }`}
                >
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{
                      background: hidden ? 'transparent' : seriesColor(s),
                      boxShadow: hidden ? `inset 0 0 0 1.5px ${seriesColor(s)}` : undefined,
                    }}
                  />
                  {s.name}
                </button>
              );
            })}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-72 w-full" />
        ) : data.length === 0 ? (
          <div className="flex h-72 items-center justify-center text-sm text-muted-foreground">
            No activity in this period.
          </div>
        ) : showTable ? (
          <div className="max-h-72 overflow-y-auto rounded-md border">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Work value</TableHead>
                  <TableHead className="text-right">Invoiced</TableHead>
                  <TableHead className="text-right">Collected</TableHead>
                  <TableHead className="text-right">Billable hours</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((m) => (
                  <TableRow key={m.month}>
                    <TableCell>{m.label}</TableCell>
                    <TableCell className="text-right">{money(m.revenue)}</TableCell>
                    <TableCell className="text-right">{money(m.invoiced)}</TableCell>
                    <TableCell className="text-right">{money(m.paid)}</TableCell>
                    <TableCell className="text-right">{hoursFmt(m.billable_hours)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              {view === 'amounts' ? (
                <LineChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--border)" strokeOpacity={0.6} />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tick={axisTick}
                    minTickGap={24}
                  />
                  <YAxis
                    width={52}
                    tickLine={false}
                    axisLine={false}
                    tick={axisTick}
                    tickFormatter={moneyCompact}
                  />
                  {tooltip}
                  {SERIES.filter((s) => !hiddenSeries.has(s.key)).map((s) => (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      name={s.name}
                      stroke={seriesColor(s)}
                      strokeWidth={2}
                      dot={{ r: 3, strokeWidth: 0, fill: seriesColor(s) }}
                      activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--card)' }}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              ) : (
                <BarChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--border)" strokeOpacity={0.6} />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tick={axisTick}
                    minTickGap={24}
                  />
                  <YAxis
                    width={44}
                    tickLine={false}
                    axisLine={false}
                    tick={axisTick}
                    tickFormatter={(v: number) => `${v}h`}
                  />
                  {tooltip}
                  <Bar
                    dataKey="billable_hours"
                    name="Billable hours"
                    fill={isDark ? HOURS_COLOR.dark : HOURS_COLOR.light}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                    isAnimationActive={false}
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
